import {
  Alert,
  Box,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { AuthButton, AuthFormContainer, AuthLink, AuthTextField } from "../components/auth";
import { useUser } from "../hooks/useAuth";
import { apiClient } from "../utils/api";
import { messageFromDrfError } from "../utils/drfErrorMessage";
import { MixpanelEvents, trackEvent } from "../utils/mixpanel";

const ResetPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const { data: user } = useUser();
  const hasValidatedToken = useRef(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    password: "",
    password_confirm: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    trackEvent(MixpanelEvents.PAGE_VIEWED, {
      page_name: "Reset Password",
      has_token: Boolean(token),
    });
  }, [token]);

  // Validate token on mount
  useEffect(() => {
    if (token && !hasValidatedToken.current) {
      hasValidatedToken.current = true;
      setValidatingToken(true);
      setError(null);

      apiClient
        .validateResetToken(token)
        .then(() => {
          setTokenValid(true);
          setValidatingToken(false);
        })
        .catch((err: unknown) => {
          setError(messageFromDrfError(err, t("common.error")));
          setTokenValid(false);
          setValidatingToken(false);
          // Redirect to forgot-password page after a delay
          setTimeout(() => {
            navigate("/forgot-password", { replace: true });
          }, 3000);
        });
    } else if (!token) {
      setValidatingToken(false);
      setError(t("common.error"));
      setTimeout(() => {
        navigate("/forgot-password", { replace: true });
      }, 3000);
    }
  }, [token, navigate, t]);

  // Redirect if user is already logged in
  useEffect(() => {
    if (user) {
      navigate("/", { replace: true });
    }
  }, [user, navigate]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.password) {
      newErrors.password = t("auth.passwordRequired");
    } else if (formData.password.length < 8) {
      newErrors.password = t("auth.passwordMinLength", "Password must be at least 8 characters");
    }

    if (formData.password !== formData.password_confirm) {
      newErrors.password_confirm = t("auth.passwordsDoNotMatch");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate() || !token || !tokenValid) return;

    setLoading(true);
    setError(null);

    try {
      await apiClient.resetPassword(
        token,
        formData.password,
        formData.password_confirm
      );
      setSuccess(true);
      // Navigate to login after successful reset
      setTimeout(() => {
        navigate("/login", { replace: true });
      }, 2000);
    } catch (err: unknown) {
      setError(messageFromDrfError(err, t("common.error")));
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while validating token
  if (validatingToken) {
    return (
      <AuthFormContainer>
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            {t("common.loading", "Loading...")}
          </Typography>
        </Box>
      </AuthFormContainer>
    );
  }

  return (
    <AuthFormContainer>
      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
      >
        <Stack spacing={{ xs: 2, sm: 2.5, md: 3 }}>
          <Stack spacing={{ xs: 0.25, md: 0.5 }}>
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{ fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2.125rem" } }}
            >
              {t("auth.resetPassword")}
            </Typography>
          </Stack>

          {success && (
            <Alert severity="success" sx={{ py: { xs: 0.5, md: 1 } }}>
              {t("auth.passwordResetSuccess")}
            </Alert>
          )}

          {error && <Alert severity="error" sx={{ py: { xs: 0.5, md: 1 } }}>{error}</Alert>}

          {!success && tokenValid ? (
            <>
              <Stack spacing={{ xs: 1.5, md: 2 }}>
                <AuthTextField
                  fullWidth
                  type="password"
                  label={t("auth.password")}
                  autoComplete="new-password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  error={!!errors.password}
                  helperText={errors.password}
                  required
                />
                <AuthTextField
                  fullWidth
                  type="password"
                  label={t("auth.confirmPassword")}
                  autoComplete="new-password"
                  value={formData.password_confirm}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      password_confirm: e.target.value,
                    })
                  }
                  error={!!errors.password_confirm}
                  helperText={errors.password_confirm}
                  required
                />
              </Stack>

              <AuthButton
                type="submit"
                variant="primary"
                loading={loading}
                disabled={!token || !tokenValid}
              >
                {t("common.submit")}
              </AuthButton>

              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  textAlign: "center",
                  fontSize: { xs: "0.75rem", md: "0.875rem" },
                  pt: { xs: 0.5, md: 0 }
                }}
              >
                <AuthLink to="/login">
                  {t("common.back")}
                </AuthLink>
              </Typography>
            </>
          ) : success ? (
            <AuthButton
              variant="primary"
              onClick={() => navigate("/login", { replace: true })}
            >
              {t("auth.login")}
            </AuthButton>
          ) : null}
        </Stack>
      </Box>
    </AuthFormContainer>
  );
};

export default ResetPasswordPage;
